import React, { useState } from 'react';
import { 
  Check, Clock, Play, SkipForward, User, Calendar,
  ChevronDown, ChevronUp, MessageSquare, Loader2, Edit2,
  Lock, FileCheck, Globe
} from 'lucide-react';
import { STEP_STATUS, STEP_STATUS_COLORS, STEP_STATUS_LABELS } from '../constants';

/**
 * SECTION 1: "Assigned To" and "Completed On" fields REMOVED from UI
 * SECTION 4: Final Hand Over step editable by all users
 *            Completed steps only editable by Admin
 * 
 * AUTO-SYNCED STEPS (Read-Only):
 * - Agreement & Documents (from Lead checklist)
 * - Photos & Listing Form (from Lead checklist)
 * - Property Listing, Quality Check, Rates & Channel Setup (from Lead OTA info)
 */
const StepCard = ({ 
  step, 
  stepData, 
  onComplete, 
  onStart, 
  onAssignUser,
  onEditStep,
  users = [],
  isAdmin = false,
  disabled = false
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editNote, setEditNote] = useState(stepData?.notes || '');

  const status = stepData?.status || STEP_STATUS.PENDING;
  const colors = STEP_STATUS_COLORS[status];

  // Check if this step is auto-synced (read-only)
  const isAutoSynced = stepData?.autoSynced || stepData?.readOnly;
  
  // Final Hand Over step is editable by all users (unless auto-synced)
  const isFinalHandOver = step.id === 'final_hand_over';
  const canEditCompletedStep = (isAdmin || isFinalHandOver) && !isAutoSynced;
  const isStepDisabled = disabled || isAutoSynced;

  const getStatusIcon = () => {
    if (isAutoSynced) {
      return <Lock size={16} className="text-blue-500" />;
    }
    switch (status) {
      case STEP_STATUS.COMPLETED:
        return <Check size={20} className="text-green-500" />;
      case STEP_STATUS.IN_PROGRESS:
        return <Play size={20} className="text-blue-500" />;
      case STEP_STATUS.SKIPPED:
        return <SkipForward size={20} className="text-yellow-500" />;
      default:
        return <Clock size={20} className="text-gray-400" />;
    }
  };

  const handleComplete = async () => {
    if (isStepDisabled || loading) return;
    setLoading(true);
    try {
      await onComplete?.(step.id, note);
      setNote('');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (isStepDisabled || loading) return;
    setLoading(true);
    try {
      await onStart?.(step.id);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await onEditStep?.(step.id, { notes: editNote });
      setEditMode(false);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper to display checklist item status
  const ChecklistItemStatus = ({ label, value }) => {
    const isComplete = String(value).toLowerCase() === 'complete';
    return (
      <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
        <span className="text-sm text-gray-600">{label}</span>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          isComplete ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
        }`}>
          {value || 'Pending'}
        </span>
      </div>
    );
  };

  return (
    <div className={`rounded-xl border-2 ${colors.border} ${colors.bg} overflow-hidden transition-all ${isAutoSynced ? 'relative' : ''}`}>
      {/* Auto-synced indicator badge */}
      {isAutoSynced && (
        <div className="absolute top-2 right-2 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
            <Lock size={10} /> Auto-synced
          </span>
        </div>
      )}
      
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${status === STEP_STATUS.COMPLETED ? 'bg-green-500' : status === STEP_STATUS.IN_PROGRESS ? 'bg-blue-500' : 'bg-gray-200'} flex items-center justify-center`}>
            {status === STEP_STATUS.COMPLETED ? (
              <Check size={20} className="text-white" />
            ) : (
              <span className="text-sm font-bold text-gray-600">{step.order}</span>
            )}
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              {step.name}
              {isAutoSynced && <Lock size={14} className="text-blue-400" />}
            </h4>
            <p className="text-sm text-gray-500">{step.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
            {STEP_STATUS_LABELS[status]}
          </span>
          {expanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-200 bg-white/50">
          {/* Auto-synced Info Banner */}
          {isAutoSynced && (
            <div className="my-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                <Lock size={16} />
                <span>This step is automatically controlled by Lead data</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Update the Lead's Property Checklist or OTA Info to change this step's status.
              </p>
            </div>
          )}
          
          {/* Checklist Items Display (for Agreement & Documents, Photos & Listing Form) */}
          {stepData?.checklistItems && Object.keys(stepData.checklistItems).length > 0 && (
            <div className="my-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <FileCheck size={16} className="text-gray-500" />
                <label className="text-xs font-medium text-gray-600 uppercase">Checklist Status</label>
              </div>
              <div className="space-y-0">
                {Object.entries(stepData.checklistItems).map(([key, value]) => {
                  const CHECKLIST_LABELS = {
                    'cancelled_check_and_bank_details': 'Cancelled Cheque And Bank Details',
                    'noc_agreement': 'NOC Agreement',
                    'lease_agreement': 'Lease Agreement',
                    'electricity_bill': 'Electricity Bill',
                    'pan_card': 'Pan Card',
                    'aadhar_card': 'Aadhar Card',
                    'gst_certificate': 'GST Certificate',
                    'property_photos': 'Property Photos',
                    'listing_form': 'Listing Form'
                  };
                  const label = CHECKLIST_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                    <ChecklistItemStatus 
                      key={key} 
                      label={label} 
                      value={value} 
                    />
                  );
                })}
              </div>
            </div>
          )}
          
          {/* OTA Items Display (for Property Listing, QC, Rates & Channel) */}
          {stepData?.otaItems && stepData.otaItems.length > 0 && (
            <div className="my-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Globe size={16} className="text-gray-500" />
                <label className="text-xs font-medium text-gray-600 uppercase">OTA Status ({stepData.otaItems.length})</label>
              </div>
              {/* Phase 13 — Progress bar for property_listing & quality_check */}
              {typeof stepData.totalCount === 'number' && stepData.totalCount > 0 && (
                <div className="mb-3" data-testid={`step-progress-${step.id}`}>
                  <div className="flex items-center justify-between text-xs font-medium text-gray-600 mb-1">
                    <span>{step.id === 'quality_check' ? 'QC Progress' : 'Listing Progress'}</span>
                    <span>{stepData.completedCount || 0} / {stepData.totalCount} OTAs ({stepData.progress ?? 0}%)</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-2 ${stepData.progress === 100 ? 'bg-emerald-500' : 'bg-blue-500'} transition-all`}
                      style={{ width: `${Math.max(0, Math.min(100, stepData.progress || 0))}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {stepData.otaItems.map((ota, idx) => {
                  // For QC step show qcStatus; for others show listing completionStatus.
                  const isQcStep = step.id === 'quality_check';
                  const rawVal = isQcStep ? ota.qcStatus : ota.completionStatus;
                  const val = String(rawVal || 'Pending');
                  const isDone = val.toLowerCase() === 'completed' || val.toLowerCase() === 'complete';
                  return (
                    <div key={idx} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-100" data-testid={`ota-row-${step.id}-${idx}`}>
                      <span className="text-sm font-medium text-gray-700">{ota.otaName}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${isDone ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {isQcStep ? `QC: ${val}` : val}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Legacy OTA Names Display (backward compatibility) */}
          {!stepData?.otaItems && stepData?.otaNames && stepData.otaNames.length > 0 && (
            <div className="my-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="text-xs font-medium text-gray-600 uppercase mb-2 block">OTAs</label>
              <div className="flex flex-wrap gap-2">
                {stepData.otaNames.map((ota, idx) => (
                  <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    {ota}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Completion Metadata - Show who completed this step */}
          {status === STEP_STATUS.COMPLETED && stepData?.completedBy && !isAutoSynced && (
            <div className="my-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-green-700 uppercase">Completed By</label>
                {canEditCompletedStep && !editMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditMode(true);
                      setEditNote(stepData?.notes || '');
                    }}
                    className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800"
                  >
                    <Edit2 size={12} /> Edit
                  </button>
                )}
              </div>
              <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                <User size={14} className="text-green-600" />
                {stepData.completedByName || stepData.completedBy}
              </p>
            </div>
          )}
          
          {/* Auto-synced completion info */}
          {status === STEP_STATUS.COMPLETED && isAutoSynced && (
            <div className="my-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                <Check size={16} />
                <span>Auto-completed from Lead data</span>
              </div>
            </div>
          )}

          {/* Edit Mode for Completed Steps */}
          {editMode && status === STEP_STATUS.COMPLETED && canEditCompletedStep && (
            <div className="my-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <label className="text-xs font-medium text-yellow-700 uppercase mb-2 block">Edit Notes</label>
              <textarea
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="Update completion notes..."
                rows={3}
                className="w-full px-3 py-2 border border-yellow-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 outline-none resize-none mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleEditSave}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  Save Changes
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Notes Section */}
          {stepData?.notes && !editMode && !isAutoSynced && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                <MessageSquare size={14} /> Completion Notes
              </label>
              <p className="text-sm text-gray-700 mt-1">{stepData.notes}</p>
            </div>
          )}

          {/* Action Buttons - Hidden for auto-synced steps */}
          {status !== STEP_STATUS.COMPLETED && !isStepDisabled && (
            <div className="space-y-3 mt-4">
              {/* Phase 13 — Steps 5 & 6 (rates_channel_setup, post_live_review)
                  use a Complete / Pending dropdown instead of the two-step
                  Start → Complete flow. */}
              {(step.id === 'rates_channel_setup' || step.id === 'post_live_review') ? (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1.5">Status</label>
                  <select
                    value={status === STEP_STATUS.COMPLETED ? 'Complete' : 'Pending'}
                    disabled={loading}
                    onChange={async (e) => {
                      if (e.target.value === 'Complete') {
                        setLoading(true);
                        try { await onComplete?.(step.id, note || 'Marked Complete'); }
                        finally { setLoading(false); }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none disabled:opacity-50"
                    data-testid={`step-dropdown-${step.id}`}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Complete">Complete</option>
                  </select>
                  {loading && (
                    <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Saving…</p>
                  )}
                </div>
              ) : (
                <>
                  {status === STEP_STATUS.PENDING && (
                    <button
                      onClick={handleStart}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />}
                      Start Step
                    </button>
                  )}

                  {status === STEP_STATUS.IN_PROGRESS && (
                    <>
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add completion notes (optional)..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none resize-none"
                      />
                      <button
                        onClick={handleComplete}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                      >
                        {loading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                        Mark as Complete
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Message for auto-synced pending steps */}
          {status !== STEP_STATUS.COMPLETED && isAutoSynced && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-700">
                <strong>To complete this step:</strong> Update the Lead's checklist or OTA info to mark all items as "Complete".
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StepCard;
